import { generateBookingSessionId } from "../AnalyticsTracking/booking-session-id";
import { getMetaContent } from "../AnalyticsTracking/utilities";
import { ClinicDetails } from "../Shared/Assets/src/types/vetstoria";
import { getModalById } from "../Shared/Modal/modal";
import { isNullOrWhiteSpace } from "../Shared/Utilities/string";

/** Sets clinic tracking data used by the event listener in vetstoria-booking-tracker.ts */
const setVetstoriaTrackingData = (
  clinicDetails: ClinicDetails,
  bookingSessionId: string
) => {
  if (!window["ani_vetstoriaData"]) {
    window["ani_vetstoriaData"] = {};
  }

  window["ani_vetstoriaData"].bookingData = {
    ...clinicDetails,
    sessionId: bookingSessionId
  };
};

/**
 * Creates a placeholder element for the Vetstoria widget script to attach to.p
 * @param vetstoriaId ID of the Vetstoria unit to load the widget for.
 */
const createWidget = (
  vetstoriaId: string,
  vetstoriaSites: string,
  multipleSitesTitle: string,
  multipleSitesDescription: string
) => {
  const widget = document.createElement("div");
  widget.id = "oabp-widget";
  widget.setAttribute("data-domain", "booking.vetstoria.com");
  widget.setAttribute(
    "data-theme-customisation",
    "11263B,11263B,11263B,ffffff,B9D5DE,00263e,00263e,00263e,00263e,00263e"
  );

  if (!isNullOrWhiteSpace(vetstoriaSites)) {
    widget.setAttribute("data-sites-encoded", "false");
    widget.setAttribute("data-sites", vetstoriaSites);
    widget.setAttribute("description-message", multipleSitesDescription);
    widget.setAttribute("force-desktop-view", "false");
    widget.setAttribute("offset", "0");
    widget.setAttribute("redirect-on-complete", "");
    widget.setAttribute("title-message", multipleSitesTitle);
  } else {
    widget.setAttribute("data-auto-mobile-redirection", "true");
    widget.setAttribute("data-force-desktop-view", "true");
    widget.setAttribute("data-mobile-message", "Touch Here to Make an Appointment");
    widget.setAttribute("data-mode", "html5");
    widget.setAttribute("data-offset", "0");
    widget.setAttribute("data-site-hash", vetstoriaId);
  }

  return widget;
};

/**
 * Creates a script element to load Vetstoria, expects a placeholder element with correct settings
 * to be present in the DOM.
 */
const createScript = (groupScript = false) => {
  const script = document.createElement("script");
  script.src = groupScript
    ? "https://booking.vetstoria.com/js/oabp-widget-group-require.min.js"
    : "https://booking.vetstoria.com/js/oabp-widget-require.min.js";
  script.id = "vetstoria-widget-loading-script";

  return script;
};

/**
 * Initialize booking buttons to trigger Vetstoria booking modals.
 * @param button The button (link) element to initialize the Vetstoria booking widget for.
 */
const initializeVetstoriaBookingButton = (button: HTMLAnchorElement) => {
  if (button.getAttribute("data-vetstoria-booking-button-initialized") !== "true") {
    button.setAttribute("data-vetstoria-booking-button-initialized", "true");

    const vetstoriaId = button.dataset.vetstoriaId ?? "";

    const vetstoriaSites = button.dataset.sites ?? "";
    const vetstoriaSitesTitle = button.dataset.messageTitle ?? "";
    const vetstoriaSitesDescription = button.dataset.messageDescription ?? "";

    const modalId = button.getAttribute("aria-controls") ?? "";
    const modal = getModalById(modalId);

    if (vetstoriaId?.length && modal) {
      button.addEventListener("click", () => {
        // For Vetstoria modals, generate the session id on click rather than on page load.
        // To allow re-opening the modal with a fresh session id.
        const bookingSessionId = generateBookingSessionId();

        // Save session id on the button to make sure it's picked up by the click tracker as well
        button.dataset.bookingSid = bookingSessionId;

        setVetstoriaTrackingData(
          {
            bId: button.dataset.clinicBid || getMetaContent("clinic-bid"),
            id: button.dataset.clinicId || getMetaContent("clinic-id"),
            name: button.dataset.clinicName || getMetaContent("clinic-name"),
            vetstoriaId
          },
          bookingSessionId
        );

        const widgetContainer = modal.element.querySelector<HTMLElement>(
          "[data-booking-modal-content]"
        );

        if (widgetContainer) {
          widgetContainer.innerHTML = ""; // Clear previous content

          const widget = createWidget(
            vetstoriaId,
            vetstoriaSites,
            vetstoriaSitesTitle,
            vetstoriaSitesDescription
          );

          const script = createScript(!isNullOrWhiteSpace(vetstoriaSites));

          widgetContainer.appendChild(widget);
          widgetContainer.appendChild(script);
        }

        modal.open();
      });
    }
  }
};

htmx.onLoad(rootElement => {
  rootElement
    .querySelectorAll<HTMLAnchorElement>("[data-vetstoria-booking-button]")
    .forEach(bookingButton => {
      initializeVetstoriaBookingButton(bookingButton);
    });
});

document.addEventListener("vetstoria-booking-script-loaded", () => {
  document
    .querySelectorAll<HTMLAnchorElement>("[data-vetstoria-booking-button]")
    .forEach(bookingButton => {
      initializeVetstoriaBookingButton(bookingButton);
    });
});
